import { Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Box } from "@mui/system";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import {
  ChartsGrid,
  ChartsLegend,
  ChartsXAxis,
  ChartsYAxis,
  lineElementClasses,
  LinePlot,
  markElementClasses,
  MarkPlot,
} from "@mui/x-charts";
import { format } from "date-fns";
import { LineChartPro } from "@mui/x-charts-pro";
import { CustomItemTooltipPercentage } from "../../../../../ResuableChartTooltip/CustomItemTooltipPercentage";
import { useSelector } from "react-redux";

interface PerformanceDashboardOverallScoreGraphProps {
  OverallScore: any;
  AverageOverallScore: any;
}

const PerformanceDashboardOverallScoreGraph = ({
  OverallScore,
  AverageOverallScore,
}: PerformanceDashboardOverallScoreGraphProps) => {
  const {
    exequteQueryContent,
    exequteQueryCompareQuery,
    exequteQueryContentQuery4,
    exequteQueryCompareQuery4,
    exequteQueryContentQuery3,
    selectedPeriod,
    selectedPage,
  } = useSelector((state: any) => state.pageSpeedPerformance);
  OverallScore = parseFloat(
    (
      exequteQueryContent?.payload?.[0]?.Overall_Speed_Performance_Score *
        100 || 0
    ).toFixed(2)
  );
  const PreviousOverallScore = parseFloat(
    (
      exequteQueryCompareQuery?.payload?.[0]?.Overall_Speed_Performance_Score *
        100 || 0
    ).toFixed(2)
  );

  const calculatePercentageChange = (
    currentScore: number,
    previousScore: number
  ): number => {
    return (currentScore / previousScore - 1) * 100;
  };
  const overallPercantageChange = calculatePercentageChange(
    OverallScore,
    PreviousOverallScore
  ); // Calculate the percentage change

  const PageSpecificOverallScores = exequteQueryContentQuery4?.payload
    ?.filter((item: any) => item.page_name === selectedPage?.page_name)
    ?.map((item: any) => item?.Overall_Speed_Performance_Score * 100);
  AverageOverallScore =
    PageSpecificOverallScores && PageSpecificOverallScores.length > 0
      ? PageSpecificOverallScores.reduce(
          (acc: any, score: any) => acc + score,
          0
        ) / PageSpecificOverallScores?.length
      : 0; // Return 0 if there are no scores

  const PreviousPageSpecificScores = exequteQueryCompareQuery4?.payload
    ?.filter((item: any) => item.page_name === selectedPage?.page_name)
    ?.map((item: any) => item.Overall_Speed_Performance_Score * 100);

  const AveragePreviousOverallScore =
    PreviousPageSpecificScores && PreviousPageSpecificScores?.length > 0
      ? PreviousPageSpecificScores?.reduce(
          (acc: any, score: any) => acc + score,
          0
        ) / PreviousPageSpecificScores?.length
      : 0; // Return 0 if there are no scores

  const pageSpecificPercantageChange = calculatePercentageChange(
    AverageOverallScore,
    AveragePreviousOverallScore
  );

  const GraphOverallScore =
    exequteQueryContentQuery3?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
      y: parseFloat((item?.Overall_Speed_Performance_Score * 100).toFixed(2)),
    })) || []; // Fallback to an empty array if data is unavailable

  const GraphPageSpecificOverallScore =
    exequteQueryContentQuery4?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        y: parseFloat((item?.Overall_Speed_Performance_Score * 100).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  // Determine which icon to display
  const renderTrendIcon = () => {
    if (OverallScore > PreviousOverallScore) {
      return <TrendingUpIcon />;
    } else if (OverallScore < PreviousOverallScore) {
      return <TrendingDownIcon />;
    } else if (OverallScore === PreviousOverallScore) {
      return <TrendingFlatIcon />;
    }
  };

  const renderPageSpecificTrendIcon = () => {
    if (AverageOverallScore > AveragePreviousOverallScore) {
      return <TrendingUpIcon />;
    } else if (AverageOverallScore < AveragePreviousOverallScore) {
      return <TrendingDownIcon />;
    } else if (AverageOverallScore === AveragePreviousOverallScore) {
      return <TrendingFlatIcon />;
    }
  };

  const getColor = (score: any) => {
    if (score <= 85) return { background: "#FDEDED", border: "#D32F2F80" }; // Red
    if (score <= 95) return { background: "#FFF4E5", border: "#ED6C0280" }; // Yellow/Orange
    return { background: "#EDF7ED", border: "#2E7D3280" }; // Green
  };

  const realUsersColor = getColor(
    selectedPage ? AverageOverallScore : OverallScore
  );
  return (
    <>
      <Box
        sx={{
          height: "100%",
          background: realUsersColor.background,
          border: `1px solid ${realUsersColor.border}`,
          padding: "1rem",
        }}
      >
        <Grid container spacing={2}>
          <Grid size={6}>
            <Typography sx={{ fontWeight: "500" }} fontSize={"15px"}>
              Overall Score
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#00000099", fontSize: "10px" }}
            >
              Reflects website speed, based on: Ads Mobile Speed, Real Users'
              Experience, and Real-Time Performance.
            </Typography>
          </Grid>
          <Grid size={6} sx={{ display: "flex" }} justifyContent={"center"}>
            <Typography variant="h3">
              {selectedPage
                ? parseFloat(AverageOverallScore.toFixed(2))
                : OverallScore}
              %
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                ml: "1rem",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  alignItems: "center",
                  display: "flex",
                  mr: "6px",
                  fontSize: "10px",
                }}
              >
                {selectedPage ? (
                  <>
                    {isNaN(pageSpecificPercantageChange) ||
                    !isFinite(pageSpecificPercantageChange)
                      ? "0%"
                      : pageSpecificPercantageChange.toFixed(2) + "%"}
                    {renderPageSpecificTrendIcon()}
                  </>
                ) : (
                  <>
                    {isNaN(overallPercantageChange) ||
                    !isFinite(overallPercantageChange)
                      ? "0%"
                      : overallPercantageChange.toFixed(2) + "%"}
                    {renderTrendIcon()}
                  </>
                )}
              </Typography>
              <Typography
                sx={{ color: "#000000", fontSize: "9px", fontWeight: "500" }}
              >
                compared to the <br />
                previous {selectedPeriod.toLowerCase()}.
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <LineChartPro
          dataset={
            selectedPage ? GraphPageSpecificOverallScore : GraphOverallScore
          }
          series={[
            {
              type: "line",
              dataKey: "y",
              color: "#2E7D32",
              showMark: true,
              curve: "linear",
            },
          ]}
          xAxis={[
            {
              scaleType: "point",
              dataKey: "x",
              disableLine: true,
              tickLabelStyle: {
                transform: "rotate(-35deg)",
                textAnchor: "end",
                fontSize: "8px",
              }, // Rotate X-axis labels
            },
          ]}
          yAxis={[
            {
              valueFormatter: (value) => `${value}%`,
              disableLine: true,
              disableTicks: true,
              position: "right",
            },
          ]}
          height={320}
          margin={{ left: 32, right: 40, top: 16, bottom: 40 }}
          sx={{
            [`& .${lineElementClasses.root}`]: {
              strokeWidth: 2,
            },
            [`& .${markElementClasses.root}`]: {
              scale: "0.6",
              strokeWidth: 2,
              "&:hover": {
                scale: "1.2",
                strokeWidth: 5,
                opacity: "0.6",
              },
            },
          }}
          leftAxis={null}
          rightAxis={{}}
        >
          <ChartsGrid horizontal />
          <CustomItemTooltipPercentage />
          <ChartsXAxis />
          <ChartsYAxis />
          <LinePlot />
          <MarkPlot />
          <ChartsLegend direction="row" />
        </LineChartPro>
      </Box>
    </>
  );
};

export default PerformanceDashboardOverallScoreGraph;
